import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import siicLogo from '../media/SIIC Logo For Dark Theme.png';
import missionImg from '../media/generated/about-mission.webp';
import loopImg from '../media/generated/approach-loop.webp';
import { Section, SectionHeading, PageHero, Card, IconBadge, CtaBand, fadeUp } from '../components/ui';
import { BrainIcon, SparkleIcon, PuzzleIcon, ChatIcon, LinkNodeIcon, ArrowUpRightIcon, BoltIcon } from '../components/icons';

const WhoWeAre = () => (
  <Section>
    <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
      <motion.div {...fadeUp}>
        <span className="text-sm font-bold tracking-widest uppercase text-purple-400">Who we are</span>
        <h2 className="text-2xl md:text-4xl font-extrabold mt-3 mb-5 text-white">
          A research-driven AI studio for education
        </h2>
        <p className="text-lg text-gray-400 leading-relaxed mb-4">
          Preative Solutions is a small, focused team in India working on one question: how can AI genuinely
          understand a learner — and what becomes possible in education once it does?
        </p>
        <p className="text-lg text-gray-400 leading-relaxed">
          We research learner modelling, adaptive explanation, and how to measure real understanding. Then we
          turn that research into products used by students, educators, and the platforms that serve them.
        </p>
      </motion.div>
      <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }}>
        <img src={missionImg} alt="Abstract illustration of connected orbiting spheres" className="w-full max-w-sm mx-auto" />
      </motion.div>
    </div>
  </Section>
);

const MissionVision = () => (
  <Section tone="raised">
    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
      <Card className="p-8">
        <span className="text-sm font-bold tracking-widest uppercase text-purple-400">Mission</span>
        <h3 className="text-xl md:text-2xl font-extrabold text-white mt-3 mb-3">
          Make AI in education understand the learner
        </h3>
        <p className="text-gray-400 leading-relaxed">
          To build AI systems that remember, adapt, and explain in the way each person learns best — bringing
          real personalization to education at every scale.
        </p>
      </Card>
      <Card className="p-8">
        <span className="text-sm font-bold tracking-widest uppercase text-purple-400">Vision</span>
        <h3 className="text-xl md:text-2xl font-extrabold text-white mt-3 mb-3">
          Personalized learning as the default
        </h3>
        <p className="text-gray-400 leading-relaxed">
          A world where every learner has access to teaching that adapts to them — where personalization is
          simply how education technology works, not a premium feature.
        </p>
      </Card>
    </div>
  </Section>
);

const Principles = () => (
  <Section>
    <SectionHeading eyebrow="What we believe" title="Four principles behind everything we do" />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
      {[
        {
          icon: BrainIcon,
          title: 'Understanding beats answering',
          desc: 'A correct answer that leaves a learner confused is a failure. We optimize for what someone understands afterwards, not for the quality of a single response.',
        },
        {
          icon: SparkleIcon,
          title: 'Personalization is the unlock',
          desc: 'The biggest gains in education come from systems that know the learner — what they know, where they struggle, how they learn — and act on it.',
        },
        {
          icon: BoltIcon,
          title: 'AI-native, not AI-added',
          desc: 'We design around what AI makes newly possible, instead of bolting a chat box onto an existing experience.',
        },
        {
          icon: PuzzleIcon,
          title: 'Leverage lives in the layer',
          desc: 'Not every problem needs another app. Sometimes the greatest impact comes from infrastructure that many other builders can stand on.',
        },
      ].map((item) => (
        <Card key={item.title} hover>
          <IconBadge icon={item.icon} className="mb-4" />
          <h3 className="font-bold text-white mb-2">{item.title}</h3>
          <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
        </Card>
      ))}
    </div>
  </Section>
);

const HowWeWork = () => (
  <Section tone="raised">
    <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
      <motion.div {...fadeUp} className="order-2 lg:order-1">
        <img src={loopImg} alt="Illustration of a continuous research, build and learn cycle" className="w-full max-w-sm mx-auto" />
      </motion.div>
      <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }} className="order-1 lg:order-2">
        <span className="text-sm font-bold tracking-widest uppercase text-purple-400">How we work</span>
        <h2 className="text-2xl md:text-4xl font-extrabold mt-3 mb-6 text-white">A loop, not a pipeline</h2>
        <div className="space-y-5">
          {[
            { title: 'Research', desc: 'We start from learning science and open questions about how AI can model a learner.' },
            { title: 'Build', desc: 'We turn findings into working systems and put them in front of real students and platforms early.' },
            { title: 'Learn', desc: 'What happens in real classrooms and real products feeds straight back into the research.' },
          ].map((item, i) => (
            <div key={item.title} className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-purple-600 text-white text-sm font-extrabold flex items-center justify-center flex-shrink-0">
                {i + 1}
              </div>
              <div>
                <h3 className="font-bold text-white mb-1">{item.title}</h3>
                <p className="text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </Section>
);

const ProductsAtAGlance = () => (
  <Section>
    <SectionHeading
      eyebrow="What we've built"
      title="Our research in the world"
      subtitle="Two live products, serving two different sides of the same problem."
    />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
      {[
        {
          icon: LinkNodeIcon,
          tone: 'amber',
          name: 'GyanSpark',
          desc: 'A personalization layer that education platforms integrate so every AI feature they run shares one understanding of each learner.',
          to: '/products/gyanspark',
        },
        {
          icon: ChatIcon,
          tone: 'purple',
          name: 'Gyanbeej',
          desc: 'An AI learning assistant used directly by students, teachers, and parents — explanations, practice, and conversation that adapt to the learner.',
          to: '/products/gyanbeej',
        },
      ].map((p) => (
        <Card key={p.name} hover className="flex flex-col">
          <IconBadge icon={p.icon} tone={p.tone} className="mb-4" />
          <h3 className="text-lg font-extrabold text-white mb-2">{p.name}</h3>
          <p className="text-sm text-gray-400 leading-relaxed mb-5 flex-1">{p.desc}</p>
          <Link
            to={p.to}
            className={`inline-flex items-center gap-1.5 text-sm font-semibold hover:underline ${
              p.tone === 'amber' ? 'text-amber-400' : 'text-purple-400'
            }`}
          >
            Learn more <ArrowUpRightIcon className="w-3.5 h-3.5" />
          </Link>
        </Card>
      ))}
    </div>
  </Section>
);

const Incubation = () => (
  <Section tone="raised">
    <SectionHeading eyebrow="Backed by" title="Incubated at SIIC, IIT Kanpur" />
    <motion.div
      {...fadeUp}
      className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center gap-8 p-8 rounded-3xl bg-white/[0.04] border border-white/10 shadow-sm"
    >
      <img src={siicLogo} alt="SIIC, IIT Kanpur logo" className="h-24 sm:h-32 w-auto max-w-sm object-contain flex-shrink-0" />
      <p className="text-gray-400 leading-relaxed text-center sm:text-left">
        Preative Solutions is an incubated startup at the Startup Incubation and Innovation Centre (SIIC),
        IIT Kanpur — one of India's leading deep-tech incubators. The programme gives us access to
        mentorship, technical infrastructure, and a network of founders building hard technology products.
      </p>
    </motion.div>
  </Section>
);

const WhereHeaded = () => (
  <Section>
    <SectionHeading
      eyebrow="What's next"
      title="Where we're headed"
      subtitle="We're early, and deliberately so. Here's what we're focused on."
    />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
      {[
        { n: '01', title: 'Deeper learner models', desc: 'Richer, longer-lived understanding of what a learner knows and how they learn.' },
        { n: '02', title: 'Personalization at scale', desc: 'Making that understanding available to every platform, not only our own products.' },
        { n: '03', title: 'Measuring what matters', desc: 'Better ways to evaluate real understanding over time, and to prove personalization works.' },
      ].map((item, i) => (
        <motion.div key={item.n} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }} className="p-6 rounded-2xl bg-white/[0.04] border border-white/10">
          <div className="text-3xl font-extrabold text-purple-200 mb-3">{item.n}</div>
          <h3 className="font-bold text-white mb-2">{item.title}</h3>
          <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
        </motion.div>
      ))}
    </div>
  </Section>
);

const About = () => (
  <>
    <Seo
      title="About Us"
      description="Preative Solutions is a research-driven AI studio for education, incubated at SIIC, IIT Kanpur. Learn about our mission, vision, principles, and how we work."
      path="/about"
    />
    <PageHero
      eyebrow="About us"
      title="AI research for how people learn"
      subtitle={
        <>
          We're a research-driven AI studio building personalization into education — incubated at{' '}
          <strong className="font-bold text-white">SIIC, IIT Kanpur</strong>.
        </>
      }
    >
      <div className="flex flex-col items-center gap-2">
        <span className="text-xs font-semibold tracking-widest uppercase text-gray-400">Incubated at</span>
        <img src={siicLogo} alt="SIIC, IIT Kanpur logo" className="h-24 sm:h-32 w-auto object-contain" />
        <span className="text-base sm:text-lg font-bold text-white mt-1">SIIC, IIT Kanpur</span>
      </div>
    </PageHero>
    <WhoWeAre />
    <MissionVision />
    <Principles />
    <HowWeWork />
    <ProductsAtAGlance />
    <Incubation />
    <WhereHeaded />
    <CtaBand title="Let's talk" subtitle="Whether you're an institution, a platform, or someone who wants to build with us.">
      <Link to="/contact" className="px-7 py-3 rounded-full bg-white text-[#1a0033] font-semibold hover:bg-purple-500/10 transition-colors">
        Contact us
      </Link>
      <Link to="/careers" className="px-7 py-3 rounded-full border border-white/30 font-semibold hover:bg-white/10 transition-colors">
        View careers
      </Link>
    </CtaBand>
  </>
);

export default About;
